import { Info } from "lucide-react";
import type * as React from "react";
import { useCallback, useMemo, useRef } from "react";

import { cn } from "@krea/shared/utils";
import { FormatNumber } from "@krea/shared/utils/formatNumber";

import { ResponsiveTooltip } from "./ResponsiveTooltip";
import { Slider } from "./Slider";
import { getClosestIndex } from "./slider-utils";

export interface ILabeledSliderProps {
  value: number;
  valuesList: number[];
  onChange: (value: number) => void;
  label?: string;
  textAppend?: string;
  tooltip?: React.ReactNode;
  className?: string;
  trackClassName?: string;
  formatter?: (value: number) => string;
  showMinMax?: boolean;
}

const LabeledSlider = ({
  value,
  valuesList,
  onChange,
  label,
  textAppend,
  tooltip,
  className,
  trackClassName,
  formatter = FormatNumber,
  showMinMax = true,
}: ILabeledSliderProps) => {
  const sliderRef = useRef<HTMLSpanElement>(null);

  const currentIndex = useMemo(
    () => getClosestIndex(valuesList, value),
    [valuesList, value],
  );

  const maxIndex = valuesList.length > 0 ? valuesList.length - 1 : 0;

  const handleSliderChange = useCallback(
    ([index]: number[]) => {
      if (index === undefined) return;
      const newValue = valuesList[index];
      if (newValue !== undefined) {
        onChange(newValue);
      }
    },
    [onChange, valuesList],
  );

  const formattedValue = textAppend
    ? `${formatter(value)} ${textAppend}`
    : String(value);

  return (
    <div className={cn("flex flex-col", className)}>
      {label && (
        <output className="flex items-baseline">
          <span className="mr-2 text-label">{`${label}:`}</span>
          <span className="flex items-center text-base font-semibold text-black">
            <span style={{ unicodeBidi: "isolate" }}>{formattedValue}</span>
            {tooltip && (
              <ResponsiveTooltip trigger={<Info className="ml-1 size-4" />}>
                {tooltip}
              </ResponsiveTooltip>
            )}
          </span>
        </output>
      )}

      <Slider
        ref={sliderRef}
        className="h-[48px]"
        trackClassName={trackClassName}
        onValueChange={handleSliderChange}
        value={[currentIndex]}
        step={1}
        min={0}
        max={maxIndex}
      />

      {showMinMax && valuesList.length > 0 && (
        <div className="flex justify-between">
          <span className="text-sm text-gray-600">
            {formatter(valuesList[0] as number)}
            {textAppend ? ` ${textAppend}` : ""}
          </span>
          <span className="text-sm text-gray-600">
            {formatter(valuesList[valuesList.length - 1] as number)}
            {textAppend ? ` ${textAppend}` : ""}
          </span>
        </div>
      )}
    </div>
  );
};

export { LabeledSlider };
